import { TFunction } from "react-i18next";

import { UploadCSVType, InventoryUploadCSVType } from "../../../../../../util/enums";

type ColumnProps = {
  key: string;
  type: "string" | "number" | "boolean" | "date" | "fleetId";
  isRequired: boolean;
  label?: string;
};

type InventoryColumnsProps = {
  [InventoryUploadCSVType.ITEMS]: ColumnProps[];
  [InventoryUploadCSVType.EXCEPTION]: ColumnProps[];
  [InventoryUploadCSVType.EXCEPTION_LOCATION]: ColumnProps[];
  [InventoryUploadCSVType.PREPARATION_METHOD]: ColumnProps[];
  [InventoryUploadCSVType.LOCATION]: ColumnProps[];
  [InventoryUploadCSVType.PACKAGE_TYPE]: ColumnProps[];
  [InventoryUploadCSVType.MODE]: ColumnProps[];
  [UploadCSVType.INVENTORY_LOCATION]: ColumnProps[];
  [UploadCSVType.INVENTORY_PACKAGE_TYPE]: ColumnProps[];
  [UploadCSVType.INVENTORY_PREPARATION_METHOD]: ColumnProps[];
  [UploadCSVType.INVENTORY_MODE]: ColumnProps[];
  [UploadCSVType.INVENTORY_EXCEPTION]: ColumnProps[];
  [UploadCSVType.INVENTORY_EXCEPTION_LOCATION]: ColumnProps[];
};

interface ColumnsProps extends InventoryColumnsProps {
  [UploadCSVType.ACCESS]: ColumnProps[];
  [UploadCSVType.FLEET]: ColumnProps[];
  [UploadCSVType.INVENTORY_LIST]: ColumnProps[];
  [UploadCSVType.INVENTORY_LOCATION]: ColumnProps[];
  [UploadCSVType.INVENTORY_PACKAGE_TYPE]: ColumnProps[];
  [UploadCSVType.INVENTORY_PREPARATION_METHOD]: ColumnProps[];
  [UploadCSVType.INVENTORY_MODE]: ColumnProps[];
  [UploadCSVType.INVENTORY_EXCEPTION]: ColumnProps[];
  [UploadCSVType.INVENTORY_EXCEPTION_LOCATION]: ColumnProps[];
  [UploadCSVType.INSTRUCTIONS]: ColumnProps[];
  [UploadCSVType.MATERIALS]: ColumnProps[];
  [UploadCSVType.SERVICES]: ColumnProps[];
  [UploadCSVType.STAFF]: ColumnProps[];
  [UploadCSVType.SHIPMENT]: ColumnProps[];
}

const ColumnsType = {
  INVENTORY_LIST: "inventoryList",
  INVENTORY_LOCATION: "inventoryLocation",
  INVENTORY_PACKAGE_TYPE: "inventoryPackageType",
  INVENTORY_PREPARATION_METHOD: "inventoryPreparationMethod",
  INVENTORY_MODE: "inventoryMode",
  INVENTORY_EXCEPTION: "inventoryException",
  INVENTORY_EXCEPTION_LOCATION: "inventoryExceptionLocation",
  FLEET: "fleet",
  SERVICES: "services",
  INSTRUCTIONS: "instructions",
  MATERIALS: "materials",
  STAFF: "staff",
  ACCESS: "access",
  SHIPMENT: "shipment",
} as const;

type ColumnsKey = keyof typeof ColumnsType;
export type ColumnsValues = typeof ColumnsType[ColumnsKey];

const GET_INPUT_COLUMNS = (translate: TFunction<"translation", undefined>): ColumnsProps => {
  const COLUMNS: ColumnsProps = {
    [UploadCSVType.SHIPMENT]: [
      { key: "container_id", type: "string", isRequired: true, label: translate("containerId") },
      { key: "shipment_type", type: "string", isRequired: false, label: translate("shipmentType") },
      {
        key: "container_numbers",
        type: "string",
        isRequired: true,
        label: translate("containerNumbers"),
      },
      {
        key: "origin",
        type: "string",
        isRequired: false,
        label: translate("origin"),
      },
      {
        key: "destination",
        type: "string",
        isRequired: false,
        label: translate("destination"),
      },
      {
        key: "customer_email",
        type: "string",
        isRequired: false,
        label: translate("customerEmail"),
      },
      {
        key: "customer_name",
        type: "string",
        isRequired: false,
        label: translate("customerName"),
      },
      {
        key: "customer_last_name",
        type: "string",
        isRequired: false,
        label: translate("customerLastName"),
      },
      {
        key: "customer_phone",
        type: "string",
        isRequired: false,
        label: translate("customerPhone"),
      },
    ],
    [UploadCSVType.ACCESS]: [
      { key: "name", type: "string", isRequired: true, label: translate("name") },
      { key: "type", type: "string", isRequired: true, label: translate("type") },
    ],

    [UploadCSVType.FLEET]: [
      {
        key: "fleetTypeId",
        type: "fleetId",
        isRequired: true,
        label: translate("type"),
      },
      {
        key: "fleetNumber",
        type: "string",
        isRequired: true,
        label: translate("fleetNumber"),
      },
      { key: "make", type: "string", isRequired: true, label: translate("make") },
      {
        key: "model",
        type: "string",
        isRequired: true,
        label: translate("model"),
      },
      {
        key: "licensePlate",
        type: "string",
        isRequired: true,
        label: translate("plateNumber"),
      },
      {
        key: "typeMode",
        type: "string",
        isRequired: true,
        label: translate("typeMode"),
      },
      {
        key: "vinNumber",
        type: "string",
        isRequired: false,
        label: translate("vinNumber"),
      },
      {
        key: "year",
        type: "number",
        isRequired: false,
        label: translate("year"),
      },
      {
        key: "engine",
        type: "string",
        isRequired: false,
        label: translate("engine"),
      },
      {
        key: "bodyType",
        type: "string",
        isRequired: false,
        label: translate("bodyType"),
      },
      {
        key: "condition",
        type: "string",
        isRequired: false,
        label: translate("condition"),
      },
      {
        key: "fuelType",
        type: "string",
        isRequired: false,
        label: translate("fuelType"),
      },
      {
        key: "ownership",
        type: "string",
        isRequired: false,
        label: translate("ownership"),
      },
      {
        key: "capacityM3",
        type: "number",
        isRequired: false,
        label: translate("capacity"),
      },
      {
        key: "outOfService",
        type: "boolean",
        isRequired: false,
        label: translate("outOfService"),
      },
    ],

    [UploadCSVType.INSTRUCTIONS]: [
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      {
        key: "description",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "when", type: "string", isRequired: true, label: translate("when") },
    ],

    [UploadCSVType.MATERIALS]: [
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      { key: "name", type: "string", isRequired: true, label: translate("name") },
      { key: "volume", type: "number", isRequired: false, label: translate("volume") },
      { key: "weight", type: "number", isRequired: false, label: translate("weight") },
      { key: "price", type: "number", isRequired: false, label: translate("price") },
      {
        key: "isItem",
        type: "boolean",
        isRequired: false,
        label: translate("isAnItem"),
      },
    ],

    [UploadCSVType.SERVICES]: [
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      {
        key: "sellDescription",
        type: "string",
        isRequired: true,
        label: translate("serviceDescription"),
      },
      {
        key: "sellType",
        type: "string",
        isRequired: false,
        label: translate("sellType"),
      },
      { key: "m3Fee", type: "number", isRequired: false, label: translate("m3Fee") },
      {
        key: "flatFee",
        type: "number",
        isRequired: false,
        label: translate("flatFee"),
      },
      {
        key: "hourlyFee",
        type: "number",
        isRequired: false,
        label: translate("hourlyFee"),
      },
      {
        key: "halfHourFee",
        type: "number",
        isRequired: false,
        label: translate("halfHourFee"),
      },
    ],

    [UploadCSVType.STAFF]: [
      {
        key: "firstName",
        type: "string",
        isRequired: true,
        label: translate("firstName"),
      },
      {
        key: "lastName",
        type: "string",
        isRequired: true,
        label: translate("lastName"),
      },
      {
        key: "email",
        type: "string",
        isRequired: true,
        label: translate("email"),
      },
      {
        key: "phone",
        type: "string",
        isRequired: true,
        label: translate("phone"),
      },
      {
        key: "position",
        type: "string",
        isRequired: true,
        label: translate("position"),
      },
      {
        key: "birthDate",
        type: "date",
        isRequired: false,
        label: `${translate("birthDate")} (dd/mm/yyyy)`,
      },
    ],

    [UploadCSVType.INVENTORY_LIST]: [],

    [InventoryUploadCSVType.ITEMS]: [
      {
        key: "code",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "name", type: "string", isRequired: true, label: translate("code") },
      { key: "volume", type: "number", isRequired: true, label: translate("volume") },
      { key: "weight", type: "number", isRequired: true, label: translate("weight") },
    ],

    [InventoryUploadCSVType.LOCATION]: [
      {
        key: "code",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "name", type: "string", isRequired: true, label: translate("code") },
    ],

    [InventoryUploadCSVType.PACKAGE_TYPE]: [
      {
        key: "code",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "name", type: "string", isRequired: true, label: translate("code") },
      {
        key: "volume",
        type: "string",
        isRequired: false,
        label: translate("volume"),
      },
    ],

    [InventoryUploadCSVType.EXCEPTION]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],

    [InventoryUploadCSVType.EXCEPTION_LOCATION]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],

    [InventoryUploadCSVType.PREPARATION_METHOD]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],

    [InventoryUploadCSVType.MODE]: [
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      {
        key: "modeDescription",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
      { key: "name", type: "string", isRequired: true, label: translate("name") },
    ],

    // Individual upload types for Inventory categories
    [UploadCSVType.INVENTORY_LOCATION]: [
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "code", type: "string", isRequired: true, label: translate("code") },
    ],

    [UploadCSVType.INVENTORY_PACKAGE_TYPE]: [
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("name"),
      },
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      {
        key: "volume",
        type: "number",
        isRequired: false,
        label: translate("volume"),
      },
    ],

    [UploadCSVType.INVENTORY_PREPARATION_METHOD]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],

    [UploadCSVType.INVENTORY_MODE]: [
      { key: "code", type: "string", isRequired: true, label: translate("code") },
      {
        key: "modeDescription",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
      { key: "name", type: "string", isRequired: true, label: translate("name") },
    ],

    [UploadCSVType.INVENTORY_EXCEPTION]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],

    [UploadCSVType.INVENTORY_EXCEPTION_LOCATION]: [
      { key: "prefix", type: "string", isRequired: true, label: translate("code") },
      {
        key: "name",
        type: "string",
        isRequired: true,
        label: translate("description"),
      },
    ],
  };

  return COLUMNS;
};

export default GET_INPUT_COLUMNS;
